"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.timeMinSchema = exports.localDateSchema = void 0;
exports.isLocalDate = isLocalDate;
exports.asLocalDate = asLocalDate;
exports.addDays = addDays;
exports.diffDays = diffDays;
exports.compareDates = compareDates;
exports.isBefore = isBefore;
exports.isAfter = isAfter;
exports.isSameOrBefore = isSameOrBefore;
exports.isSameOrAfter = isSameOrAfter;
exports.dayOfWeek = dayOfWeek;
exports.startOfWeekMon = startOfWeekMon;
exports.endOfWeekSun = endOfWeekSun;
exports.weekDates = weekDates;
exports.weekdayDates = weekdayDates;
exports.todayInTimezone = todayInTimezone;
exports.minToHHMM = minToHHMM;
exports.hhmmToMin = hhmmToMin;
exports.minToHumanTime = minToHumanTime;
exports.overlapsMinutes = overlapsMinutes;
const zod_1 = require("zod");
const enums_js_1 = require("./enums.js");
const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
exports.localDateSchema = zod_1.z
    .string()
    .regex(LOCAL_DATE_RE, 'Date must be YYYY-MM-DD')
    .transform((value) => value);
function isLocalDate(value) {
    return typeof value === 'string' && LOCAL_DATE_RE.test(value);
}
function asLocalDate(value) {
    if (!LOCAL_DATE_RE.test(value)) {
        throw new Error(`Invalid LocalDate: ${value}`);
    }
    return value;
}
function parts(date) {
    const match = LOCAL_DATE_RE.exec(date);
    if (!match)
        throw new Error(`Invalid LocalDate: ${date}`);
    return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
function fromUTC(y, m, d) {
    return `${y}-${pad2(m)}-${pad2(d)}`;
}
function dateToUTC(date) {
    const { y, m, d } = parts(date);
    return Date.UTC(y, m - 1, d);
}
function utcToLocalDate(utcMs) {
    const dt = new Date(utcMs);
    return fromUTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}
function addDays(date, days) {
    return utcToLocalDate(dateToUTC(date) + days * 86_400_000);
}
function diffDays(a, b) {
    return Math.round((dateToUTC(a) - dateToUTC(b)) / 86_400_000);
}
function compareDates(a, b) {
    return dateToUTC(a) - dateToUTC(b);
}
function isBefore(a, b) {
    return compareDates(a, b) < 0;
}
function isAfter(a, b) {
    return compareDates(a, b) > 0;
}
function isSameOrBefore(a, b) {
    return compareDates(a, b) <= 0;
}
function isSameOrAfter(a, b) {
    return compareDates(a, b) >= 0;
}
/** ISO weekday: MON=1 .. SUN=7. We derive purely from the date parts. */
function dayOfWeek(date) {
    const { y, m, d } = parts(date);
    // Zeller-style via UTC; deterministic, no TZ
    const utcDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun..6=Sat
    // shift to MON=0..SUN=6
    const monIdx = (utcDay + 6) % 7;
    return enums_js_1.AllDayOrder[monIdx];
}
function startOfWeekMon(date) {
    const dow = dayOfWeek(date);
    const idx = enums_js_1.AllDayOrder.indexOf(dow);
    return addDays(date, -idx);
}
function endOfWeekSun(date) {
    return addDays(startOfWeekMon(date), 6);
}
function weekDates(weekStart) {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}
function weekdayDates(weekStart) {
    return enums_js_1.WeekdayOrder.map((_, i) => addDays(weekStart, i));
}
/** Today as a LocalDate in the given IANA tz (defaults to clinic local). */
function todayInTimezone(timezone) {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    return formatter.format(now);
}
// ─── Time-of-day helpers (minutes from midnight) ──────────
exports.timeMinSchema = zod_1.z.number().int().min(0).max(24 * 60);
function minToHHMM(min) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${pad2(h)}:${pad2(m)}`;
}
function hhmmToMin(value) {
    const [h, m] = value.split(':');
    if (h === undefined || m === undefined)
        throw new Error(`Invalid time: ${value}`);
    const hour = Number(h);
    const minute = Number(m);
    if (Number.isNaN(hour) || Number.isNaN(minute))
        throw new Error(`Invalid time: ${value}`);
    return hour * 60 + minute;
}
function minToHumanTime(min) {
    const h24 = Math.floor(min / 60);
    const m = min % 60;
    const ampm = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    return `${h12}:${pad2(m)} ${ampm}`;
}
function overlapsMinutes(aStart, aEnd, bStart, bEnd) {
    return aStart < bEnd && bStart < aEnd;
}
//# sourceMappingURL=localDate.js.map